import type { PublicAppRuntimeConfig } from './app-config'

type SharedAppStore = {
  isWiki: string
  cookieHost: string
  myCountry: string
  serverbfp: string
  user: {
    userId?: string | number | null
    userVip?: string | number | boolean | null
  } | null
  userVip: string | number | boolean
  LanguageCode: string
  CountryCode: string
}

type MatomoQueueCommand = [string, ...unknown[]]
type MatomoQueue = MatomoQueueCommand[]

export type MatomoEventValue = string | number | boolean | null | undefined
export type MatomoEventPayload = Record<string, MatomoEventValue>

export type MatomoTrackPageViewInput = {
  url?: string
  title?: string
}

export type MatomoClient = {
  getSiteId: () => string
  init: () => void
  isEnabled: () => boolean
  matomofn: (eventName: string, payload?: MatomoEventPayload) => void
  trackEvent: (eventName: string, payload?: MatomoEventPayload) => void
  trackPageView: (input?: MatomoTrackPageViewInput) => void
}

export type MatomoRuntimeOptions = Pick<PublicAppRuntimeConfig, 'matomoEnabled' | 'matomoSiteId' | 'matomoTrackerUrl'>

type CreateMatomoClientOptions = MatomoRuntimeOptions & {
  host: () => string
  store: SharedAppStore
}

const DEFAULT_BFP = '822ca7b3261a0b2ec10c8cdabfb5a0ea'
const DEFAULT_TRACKER_URL = 'https://matomo.eeeoeee.com/'
const MATOMO_SCRIPT_ID = 'wikifx-matomo-script'

const DOMAIN_MATOMO_SITE_ID_MAP = [
  ['localhost', '1'], // 本地必须localhost才会触发
  ['0.0.0.0', '1'], // 本地必须localhost才会触发
  ['wikifx.com', '1'],
  ['wikifxact.com', '2'],
  ['fxeyetips.com', '3'],
  ['wikifx.vip', '4'],
  ['fxeye2017.com', '5'],
  ['wikifx2.com', '6'],
  ['shareowen.com', '7'],
  ['mchgi.com', '8'],
  ['wikifx25.com', '9'],
  ['fxeye25.com', '10'],
  ['fxeyema.com', '11'],
  ['fxeyecn.com', '12'],
  ['wikifxvn.com', '13'],
  ['wikifxvi.com', '14'],
  ['wikifxen.com', '15'],
  ['wikifxzh.com', '16'],
  ['wikifxka.com', '17'],
  ['wikifxcn.com', '18'],
  ['fxeye110.com', '19'],
  ['fxeye123.com', '20'],
  ['fxeye777.com', '21'],
  ['fxeye688.com', '22'],
  ['fxeye.com', '23'],
  ['fxeyevn.com', '24'],
  ['fxeyevi.com', '25'],
  ['fxeye555.com', '26'],
  ['fxeye2021.com', '27'],
  ['fxeyefx.com', '28'],
  ['fxeyevps.com', '29'],
  ['wikifx.hk', '30'],
  ['wikifxtips.com', '31'],
  ['fxeyevip.com', '33'],
  ['fxeyeka.com', '34'],
  ['wikifx.ltd', '39'],
  ['wikifx.club', '40'],
  ['wikifx.site', '41'],
  ['wikifx.space', '42'],
  ['wikifx.me', '43'],
  ['wikifx.xin', '44'],
  ['skylineguide.org', '49'],
  ['wikibit.com', '75'],
] as const

const normalizeTrackerUrl = (trackerUrl: string) => {
  if (!trackerUrl) {
    return DEFAULT_TRACKER_URL
  }

  return trackerUrl.endsWith('/') ? trackerUrl : `${trackerUrl}/`
}

const normalizeHost = (host: string) => {
  const value = host.trim().toLowerCase()

  if (!value) {
    return ''
  }

  return (
    value
      .replace(/^https?:\/\//u, '')
      .split('/')[0]
      ?.split(':')[0] ?? ''
  )
}

const getCookie = (name: string) => {
  if (typeof document === 'undefined') {
    return ''
  }

  const matches = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/gu, '\\$1')}=([^;]*)`),
  )

  return matches ? decodeURIComponent(matches[1] ?? '') : ''
}

const removeCookie = (name: string, domain?: string) => {
  if (typeof document === 'undefined') {
    return
  }
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`
  if (domain) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${domain}`
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`
  }
}

const setCookie = (name: string, value: string, domain?: string) => {
  if (typeof document === 'undefined') {
    return
  }
  let cookieString = `${name}=${encodeURIComponent(value)}; path=/;`
  if (domain) {
    cookieString += ` domain=.${domain};`
  }
  document.cookie = cookieString
}

const resolveBasicData = (store: SharedAppStore, bfp: string) => {
  if (store.isWiki === '1' || store.cookieHost.includes('wikifx.hk')) {
    return `999,0,2,999,0,${bfp},0`
  }

  if (store.myCountry === 'cn') {
    return `999,0,1,999,1,${bfp},1`
  }

  return `999,0,2,999,0,${bfp},0`
}

const resolveVipFlag = (store: SharedAppStore) => {
  const userData: unknown = store.user
  const userVip = userData && typeof userData === 'object' && 'userVip' in userData ? userData.userVip : store.userVip
  return userVip === true || userVip === 1 || userVip === '1' ? 1 : 0
}

const resolveTraceId = () => getCookie('setakamaitraceid')

const resolveMatomoPayload = (store: SharedAppStore, payload: MatomoEventPayload = {}) => {
  const bfp = getCookie('wiki-bfp') || store.serverbfp || DEFAULT_BFP
  const serializedPayload = Object.entries(payload)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `&${key}=${String(value)}`)
    .join('')

  return {
    category: `userid=${store.user?.userId || ''}&basicdata=${resolveBasicData(store, bfp)}&Langugecode=${
      store.LanguageCode
    }&countryCode=${store.CountryCode || getCookie('CountryCode') || ''}&akamai_trace_id=${resolveTraceId()}&isVip=${resolveVipFlag(store)}${serializedPayload}`,
    traceId: resolveTraceId(),
  }
}

const resolveUrl = (url?: string) => {
  if (typeof window === 'undefined') {
    return url || ''
  }

  if (!url) {
    return window.location.href
  }

  try {
    return new URL(url, window.location.origin).toString()
  } catch {
    return url
  }
}

const getQueue = (): MatomoQueue => {
  const currentWindow = window as Window
  currentWindow._paq = currentWindow._paq || []
  return currentWindow._paq
}

export const resolveMatomoSiteId = (host: string, overrideSiteId = '') => {
  if (overrideSiteId) {
    return overrideSiteId
  }

  const normalizedHost = normalizeHost(host)

  for (const [domain, siteId] of DOMAIN_MATOMO_SITE_ID_MAP) {
    if (normalizedHost.includes(domain)) {
      return siteId
    }
  }

  return ''
}

export const createMatomoClient = ({
  host,
  matomoEnabled,
  matomoSiteId,
  matomoTrackerUrl,
  store,
}: CreateMatomoClientOptions): MatomoClient => {
  let initialized = false
  let lastTrackedUrl = ''
  let resolvedSiteId = ''

  const trackerUrl = normalizeTrackerUrl(matomoTrackerUrl)

  const getSiteId = () => {
    if (!resolvedSiteId) {
      resolvedSiteId = resolveMatomoSiteId(host(), matomoSiteId)
    }

    return resolvedSiteId
  }

  // const setTraceDimension = () => {
  //   const traceId = resolveTraceId()

  //   if (traceId) {
  //     getQueue().push(['setCustomDimension', 1, traceId])
  //   }

  //   return traceId
  // }

  const init = () => {
    if (typeof window === 'undefined') {
      return
    }

    const siteId = getSiteId()

    if (!matomoEnabled || !siteId) {
      return
    }

    if (!initialized) {
      const queue = getQueue()
      queue.push(['enableHeartBeatTimer', 5])
      queue.push(['enableLinkTracking'])

      queue.push(['setTrackerUrl', `${trackerUrl}matomo.php`])
      queue.push(['setSiteId', siteId])
      const traceId = resolveTraceId()

      if (traceId) {
        queue.push(['setCustomDimension', 1, traceId])
      }
      //拿cookie 删除cookie，
      const fromSource = getCookie('from_source')
      if (fromSource) {
        queue.push(['setCustomDimension', 2, fromSource])
        removeCookie('from_source', store.cookieHost)
      }

      initialized = true
    }

    if (document.getElementById(MATOMO_SCRIPT_ID)) {
      return
    }

    const script = document.createElement('script')
    const firstScript = document.getElementsByTagName('script')[0]

    script.id = MATOMO_SCRIPT_ID
    script.async = true
    script.src = `${trackerUrl}matomo.js`

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
      return
    }

    document.head.appendChild(script)
  }

  const trackPageView = (input: MatomoTrackPageViewInput = {}) => {
    const currentUrl = resolveUrl(input.url)

    if (!matomoEnabled || !getSiteId() || !currentUrl || currentUrl === lastTrackedUrl) {
      return
    }

    lastTrackedUrl = currentUrl

    const queue = getQueue()

    queue.push(['setCustomUrl', currentUrl])
    queue.push(['setDocumentTitle', input.title || document.title])
    queue.push(['trackPageView'])
  }

  const trackEvent = (eventName: string, payload: MatomoEventPayload = {}) => {
    console.log('eventName', eventName, payload)
    setCookie('from_souce', eventName, store.cookieHost)

    if (!eventName) {
      return
    }

    if (!matomoEnabled || !getSiteId()) {
      return
    }

    const queue = getQueue()
    const { category, traceId } = resolveMatomoPayload(store, payload)
    queue.push(['trackEvent', category, 'Click', eventName])
    if (traceId) {
      queue.push(['setCustomDimension', 1, traceId])
    }
  }

  return {
    getSiteId,
    init,
    isEnabled: () => matomoEnabled && !!getSiteId(),
    matomofn: trackEvent,
    trackEvent,
    trackPageView,
  }
}

declare module '#app' {
  interface NuxtApp {
    $matomo: MatomoClient
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $matomo: MatomoClient
    matomofn: MatomoClient['matomofn']
  }
}

declare global {
  interface Window {
    _paq?: MatomoQueue
    matomofn?: MatomoClient['matomofn']
  }
}
